/// <amd-module name="VSS/Diagnostics/Dialog/FaultInjectionDialog" />
import * as React from "react";
import { IVssPerformanceDiagnosticsService, PerfSettingsProps } from "VSS/Diagnostics/PerfBar";
import { Components } from "VSS/Platform/Layout";
import { Dialog, IDialogProps } from "VSSUI/Dialog";
import { ListSelection } from "VSSUI/List";
import { Button } from "VSSUI/Button";
import { getCookie } from "VSS/Platform/Util/Cookie";
import { IFaultItem, FaultInjectionFaultInputs } from "VSS/Diagnostics/Dialog/FaultInjectionFaultInputs";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Card } from "VSSUI/Card";
import { Table, ColumnSelect, ColumnFill, renderSimpleCell, ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import * as FaultInjectionResources from "VSS/Diagnostics/Dialog/Resources";

interface IFaultInjectionDialogProps {
    onDismiss: () => any;
    onFaultChanged: (on: boolean) => any;
}

class FaultInjectionDialog extends React.Component<IFaultInjectionDialogProps> {
    private static readonly faultInjectionCookie = "AzDevRequestFaults";

    constructor(props: IFaultInjectionDialogProps) {
        super(props);
        var currentCookie = getCookie(FaultInjectionDialog.faultInjectionCookie);
        this.faultItems= currentCookie ? new ObservableArray<IFaultItem>(JSON.parse(currentCookie)) : new ObservableArray<IFaultItem>()
    }

    private faultListSelection = new ListSelection(true);
    private faultItems: ObservableArray<IFaultItem>;

    private faultTableColumns: Array<ITableColumn<any>> = [
        new ColumnSelect(),
        { id: "faultPoint", name: "Fault Point", width: new ObservableValue(100), renderCell: renderSimpleCell },
        { id: "faultType", name: "Fault Type", width: new ObservableValue(100), renderCell: renderSimpleCell },
        { id: "settings", name: "Fault Settings", width: new ObservableValue(-100), renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IFaultItem>, tableItem: IFaultItem): JSX.Element => {
            return (
                <SimpleTableCell
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                    key={"col-" + columnIndex}
                    contentClassName="fontSizeM scroll-hidden"
                >
                    <div className="flex-row scroll-hidden">
                        <Tooltip overflowOnly={true}>
                            <span className="text-ellipsis">{JSON.stringify(tableItem.settings)}</span>
                        </Tooltip>
                    </div>
                </SimpleTableCell>
            );
        } }
    ];
    
    private faultListBarItems: IHeaderCommandBarItem[] = [
        {
            iconProps: {
                iconName: "Save"
            },
            id: "faultListSave",
            important: true,
            isPrimary: true,
            onActivate: () => {
                this.saveFaults();
            },
            text: FaultInjectionResources.SaveFaultListButtonText,
            tooltipProps: {
                text: FaultInjectionResources.SaveFaultListButtonTipText
            },
    
        },
        {
            iconProps: {
                iconName: "Delete"
            },
            id: "faultListDelete",
            important: true,
            onActivate: () => {
                this.deleteSelectedItems();
            },
            text: FaultInjectionResources.DeleteFaultListButtonText,
            tooltipProps: {
                text: FaultInjectionResources.DeleteFaultListButtonTipText
            },
        }
    ];

    public render(): JSX.Element {
        const dialogProps: IDialogProps = {
            calloutContentClassName: "performance-dialog dialog-full-size",
            lightDismiss: false,
            showCloseButton: true,
            titleProps: { },
            onDismiss: this.props.onDismiss
        };

        return (
            <Dialog {...dialogProps}>
                <div className="flex-column flex-grow">
                    <Header
                        title={ FaultInjectionResources.FaultInjectionHeaderTitle }
                        commandBarItems={this.faultListBarItems}
                        titleSize={TitleSize.Medium}
                        description={this.renderHeaderDescription()}
                        titleClassName="fontSizeLL"
                        />
                    <div className="fault-injection-hub flex-grow flex-column">
                        <div className="fault-injection-faults flex-column flex-grow">
                            <Card 
                                className="flex-column flex-grow"
                                titleProps={{ text: FaultInjectionResources.FaultInjectionFaultListCardTitle}}
                                headerDescriptionProps={{text: FaultInjectionResources.FaultInjectionFaultListCardDescription}}
                            >
                                <div className="fault-injection-faults-list flex-grow flex-column scroll-auto">
                                    <Table itemProvider={this.faultItems} columns={this.faultTableColumns} selection={this.faultListSelection} />
                                </div>
                            </Card>
                        </div>
                        <div className="fault-injection-helper flex-column">
                            <FaultInjectionFaultInputs onFaultAdd={this.inputChange.addToFaultList} />
                        </div>
                    </div>
                </div>
            </Dialog>
        );
    }

    private inputChange = {
        addToFaultList: (faultItem: IFaultItem): void => {
            this.faultItems.push(faultItem);
        }
    };

    private setCookieState = (name: string, state: boolean | string) => {
        document.cookie = `${name}=${state};path=/`;
    };

    private deleteSelectedItems = () => {
        for(let i = 0; i < this.faultItems.length; i++) {
            if(this.faultListSelection.selected(i)) {
                this.faultItems.splice(i, 1);
                i--;
            }
        }
    };

    private saveFaults = () => {
        try {
            if (this.faultItems && this.faultItems.length > 0) {
                this.setCookieState(FaultInjectionDialog.faultInjectionCookie, JSON.stringify(this.faultItems.value));
                this.props.onFaultChanged(true);
                window.location.reload();
            } else {
                this.setCookieState(FaultInjectionDialog.faultInjectionCookie, "");
                this.props.onFaultChanged(false);
                window.location.reload();
            }
        } catch {
            console.warn("Fault profile input is not a valid json string.");
        }
    };

    private renderHeaderDescription = (): JSX.Element => {
        return (
            <div>
                This is a UI tool that helps configure request scoped faults. For detailed information about this type of fault injection, please check{" "}
                <a
                    target="_blank"
                    href="https://dev.azure.com/mseng/AzureDevOps/_wiki/wikis/AzureDevOps.wiki?pagePath=%2FTeam%20Pages%2FService%20Platform%2FReliable%20Adaptive%20and%20Secure%20Platform%2FRequest%20Scoped%20Fault%20Injection&wikiVersion=GBwikiMaster"
                >
                    Request Scoped Fault Injection
                </a>
                .
            </div>
        );
    }
}

Components.add("vss.perf-fault-dialog", FaultInjectionDialog);
